import type { QueryClient, UseQueryResult } from "@tanstack/react-query";
import type { AxiosInstance } from "axios";
import { EventIdPublic } from "@shared/schemas/branded-id.schemas";
import {
  AdminEventParticipantRosterSchema,
  type AdminEventParticipantRoster,
} from "@shared/schemas/admin-event-participants.schemas";
import { BaseApiHooksRepository } from "../../base-api-hooks.repository";
import { resolvePhotoUrls } from "@shared/utils/participant-photo-url.utils";
import { createEventParticipantsAdminApi } from "./event-participants-admin.api";

/**
 * Exported so any admin feature rendering the roster can invalidate it after
 * mutating the participant set (import, removal). The roster is not owned by
 * one feature — nothing here may key off a consuming feature's cache prefix.
 */
export const EVENT_PARTICIPANTS_ADMIN_QUERY_KEYS = {
  byEvent: (eventIdPublic: EventIdPublic) =>
    ["event-participants-admin", "event", eventIdPublic] as const,
} as const;

// The roster changes on import/removal, never on pipeline progress or intake
// activity, so it outlives the polling any one consumer does alongside it.
const ROSTER_STALE_TIME_MS = 5 * 60_000;

export class EventParticipantsAdminHooksHttpRepositoryImpl extends BaseApiHooksRepository {
  private readonly api;

  constructor(queryClient: QueryClient, apiClient: AxiosInstance) {
    super(queryClient);
    this.api = createEventParticipantsAdminApi(apiClient);
  }

  createHooks() {
    const { api } = this;
    return {
      useGetEventParticipants: (
        eventIdPublic: EventIdPublic,
      ): UseQueryResult<AdminEventParticipantRoster> =>
        this.query({
          key: EVENT_PARTICIPANTS_ADMIN_QUERY_KEYS.byEvent(eventIdPublic),
          fn: () => api.getEventParticipants(eventIdPublic),
          // The backend ships the stored `evt-participant-photo://` placeholder
          // verbatim; only the client knows the event to resolve it against.
          parse: (dto) => ({
            participants: resolvePhotoUrls(
              AdminEventParticipantRosterSchema.parse(dto).participants,
              EventIdPublic.unwrap(eventIdPublic),
            ),
          }),
          staleTime: ROSTER_STALE_TIME_MS,
        }),
    };
  }
}
